/**
 * Sign in.
 *
 * ANTI-ENUMERATION IS A UI CONCERN, NOT ONLY AN API ONE. `/auth/request-link`
 * answers `{ sent: true }` whether or not the address belongs to a user, on
 * purpose — an endpoint that distinguishes the two is an oracle for testing
 * whether a given seller is our client. Every word of the confirmation below is
 * written to preserve that: it says what happens IF the address is registered
 * and never that anything was found. "We've sent you a link" would undo the
 * server-side property entirely.
 */

import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { requestMagicLink } from '../api.js';
import { ErrorCallout, Field } from '../ui.js';

export function LoginPage({ onSignedIn }: { onSignedIn: () => void }): ReactNode {
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await requestMagicLink(email.trim());
      setSentTo(email.trim());
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  if (sentTo) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <h1>Check your email</h1>
          <p>
            If <strong>{sentTo}</strong> belongs to an account, a sign-in link is on its way. It
            expires in 15 minutes and works once.
          </p>

          <div className="stack">
            <p className="field-hint">
              We do not say whether an address is registered — that would let anyone test who our
              clients are. Nothing below is a sign that the address was or was not found.
            </p>
            <p className="field-hint">
              Opening the link signs you in in that browser. Come back to this tab and continue once
              you have.
            </p>
            <div className="form-actions">
              <button className="btn btn-primary" onClick={onSignedIn}>
                I have opened the link
              </button>
              <button
                className="btn btn-quiet"
                onClick={() => {
                  setSentTo(null);
                  setError(null);
                }}
              >
                Use a different address
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <form className="login-card stack" onSubmit={(event) => void submit(event)}>
        <div>
          <h1>FBT Recovery</h1>
          <p className="muted">
            Sign in with a link sent to your email. There is no password to lose.
          </p>
        </div>

        {error ? <ErrorCallout error={error} /> : null}

        <Field label="Work email" hint="The address your account was created with.">
          {({ id, describedBy }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              type="email"
              name="email"
              value={email}
              autoComplete="email"
              autoFocus
              required
              placeholder="you@company.com"
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <button className="btn btn-primary" type="submit" disabled={submitting || !email.trim()}>
          {submitting ? 'Sending…' : 'Email me a sign-in link'}
        </button>
      </form>
    </div>
  );
}
